import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  List as ListIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { trailsService } from '../services/trailsService';
import uploadService from '../services/uploadService';
import { Trail } from '../types';

const EditTrail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [, setTrail] = useState<Trail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnailUrl: '',
    isActive: true,
    isPremium: false,
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchTrail = useCallback(async () => {
    try {
      const data = await trailsService.getOne(id!);
      setTrail(data);
      setFormData({
        title: data.title,
        description: data.description,
        thumbnailUrl: data.thumbnailUrl || '',
        isActive: data.isActive,
        isPremium: data.isPremium || false,
      });
    } catch (err: any) {
      setError('Erro ao carregar trilha');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew && id) {
      fetchTrail();
    }
  }, [id, isNew, fetchTrail]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError('Tipo de arquivo não permitido. Use: JPG, PNG, WebP ou GIF');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo: 5MB');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const url = await uploadService.uploadImage(file, 'trails');
      setFormData({ ...formData, thumbnailUrl: url });
    } catch (err: any) {
      setError('Erro ao fazer upload da imagem');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, thumbnailUrl: '' });
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const newTrail = await trailsService.create(formData);
        navigate(`/trails/${newTrail.id}/devotionals`);
      } else {
        await trailsService.update(id!, formData);
        setError('');
        // Mostra mensagem de sucesso temporariamente
        setError('');
        const successMsg = 'Trilha salva com sucesso!';
        setError(successMsg);
        setTimeout(() => setError(''), 3000);
      }
    } catch (err: any) {
      setError('Erro ao salvar trilha');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/devotionals')}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 150 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.25rem', sm: '2rem' } }}>
              {isNew ? 'Nova Trilha' : 'Editar Trilha'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {isNew ? 'Crie uma nova trilha de devocionais' : 'Edite as informações da trilha'}
            </Typography>
          </Box>
        </Box>
        {!isNew && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ListIcon />}
              onClick={() => navigate(`/trails/${id}/devotionals`)}
              size="small"
              sx={{
                borderColor: '#4CAF50',
                color: '#4CAF50',
                '&:hover': {
                  borderColor: '#43A047',
                  background: 'rgba(76, 175, 80, 0.04)',
                },
              }}
            >
              Devocionais
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert
          severity={error.includes('sucesso') ? 'success' : 'error'}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Informações da Trilha
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Título"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
              placeholder="Ex: Bíblia em 1 Ano"
            />

            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              multiline
              rows={4}
              InputLabelProps={{ shrink: true }}
              placeholder="Descreva o objetivo e conteúdo desta trilha..."
            />

            {/* Upload de Imagem */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Imagem de Capa
              </Typography>

              {formData.thumbnailUrl ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={formData.thumbnailUrl}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      height: 'auto',
                      aspectRatio: '5/3',
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    aspectRatio: '5/3',
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    bgcolor: '#fafafa',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#4CAF50',
                      bgcolor: 'rgba(76, 175, 80, 0.04)',
                    },
                  }}
                >
                  {uploading ? (
                    <CircularProgress size={40} />
                  ) : (
                    <>
                      <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                      <Typography color="text.secondary">
                        Clique para fazer upload
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        JPG, PNG, WebP ou GIF (máx. 5MB)
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Dimensão recomendada: 1200x720 pixels (proporção 5:3)
              </Typography>
            </Box>

            {/* Toggles */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Ativar/Desativar Trilha */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: formData.isActive ? 'rgba(76, 175, 80, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid',
                  borderColor: formData.isActive ? 'rgba(76, 175, 80, 0.3)' : 'divider',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Trilha Ativa
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.isActive ? 'Visível para os usuários no app' : 'Oculta — não aparece no app'}
                      </Typography>
                    </Box>
                  }
                  sx={{ ml: 0, flex: 1 }}
                />
              </Box>

              {/* Premium */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: formData.isPremium ? 'rgba(255, 179, 0, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid',
                  borderColor: formData.isPremium ? 'rgba(255, 179, 0, 0.3)' : 'divider',
                }}
              >
                <StarIcon sx={{ color: formData.isPremium ? '#FFB300' : '#ccc' }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPremium}
                      onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFB300' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFB300' },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Trilha Premium
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Apenas assinantes premium terão acesso a esta trilha
                      </Typography>
                    </Box>
                  }
                  sx={{ ml: 0, flex: 1 }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/devotionals')}
                sx={{
                  borderColor: '#ccc',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: '#999',
                    background: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43A047 0%, #5CB860 100%)',
                  },
                }}
              >
                {saving ? 'Salvando...' : isNew ? 'Criar Trilha' : 'Salvar Alterações'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditTrail;
